import React from "react";
import CallToActionBanner from "../components/CallToActionBanner";

const services = [
  {
    title: "Network Design & Cabling",
    description: "Structured LAN/WAN network planning, copper/fiber cabling, and topologies tailored for reliability and scalability.",
    image: "/assets/service-cabling.png",
    icon: "🌐",
    features: ["Custom network design", "Fiber/copper cabling", "Future-proof architecture"]
  },
  {
    title: "Firewall & Network Security",
    description: "Deployment of Fortinet, Sophos, or Cisco firewalls with secure VPN, intrusion prevention, and remote monitoring.",
    image: "/assets/service-firewall.png",
    icon: "🛡️",
    features: ["Enterprise firewalls", "24/7 threat monitoring", "VPN solutions"]
  },
  {
    title: "CCTV & Surveillance",
    description: "Comprehensive CCTV installation and monitoring services for enhanced security and peace of mind.",
    image: "/assets/service-backup.png",
    icon: "📹",
    features: ["High-definition cameras", "24/7 monitoring", "Remote access"]
  },
  {
    title: "Server Rack & Infrastructure",
    description: "Neat, efficient server room setup including rack management, patch panels, structured labeling, and ventilation planning.",
    image: "/assets/service-server.png",
    icon: "🖥️",
    features: ["Rack optimization", "Cable management", "Cooling solutions"]
  },
  {
    title: "Wi-Fi & Access Point Deployment",
    description: "Seamless wireless connectivity using managed access points with roaming, mesh networks, and controller integration.",
    image: "/assets/service-wifi.png",
    icon: "📶",
    features: ["Enterprise Wi-Fi", "Mesh networks", "Performance tuning"]
  },
  {
    title: "IT AMC & On-Demand Support",
    description: "Annual Maintenance Contracts for full-stack IT support including preventive maintenance and issue resolution.",
    image: "/assets/service-amc.png",
    icon: "🔧",
    features: ["24/7 support", "Preventive maintenance", "Rapid response"]
  },
];

const ServicesPage = () => {
  return (
    <div className="text-gray-800">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-primary to-primary-dark text-white py-24 px-6 text-center overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-1/4 w-40 h-40 rounded-full bg-accent/20 animate-pulse"></div>
          <div className="absolute bottom-10 right-1/4 w-48 h-48 rounded-full bg-white/10 animate-pulse delay-300"></div>
        </div>
        <div className="max-w-4xl mx-auto relative z-10">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-white to-gray-200">
              Our IT Services
            </span>
          </h1>
          <p className="text-xl text-white/90 max-w-3xl mx-auto leading-relaxed">
            Expert solutions tailored to meet your IT infrastructure and networking needs with enterprise-grade reliability.
          </p>
          <div className="mt-8 flex justify-center gap-4">
            <a 
              href="#services" 
              className="px-6 py-3 bg-white text-primary font-semibold rounded-lg hover:bg-gray-100 transition-all"
            >
              Explore Services
            </a>
            <a 
              href="/contact" 
              className="px-6 py-3 border-2 border-white/30 text-white font-semibold rounded-lg hover:bg-white/10 transition-all"
            >
              Get a Quote
            </a>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section id="services" className="py-20 px-6 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-gray-900">
              Comprehensive <span className="text-accent">IT Solutions</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              We deliver end-to-end technology services designed to keep your business connected, secure, and productive.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div
                key={index}
                className="group bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden hover:-translate-y-2"
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                  <div className="absolute top-4 right-4 bg-white/90 text-2xl w-12 h-12 rounded-lg flex items-center justify-center shadow-sm">
                    {service.icon}
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    {service.title}
                  </h3>
                  <p className="text-gray-600 mb-4">
                    {service.description}
                  </p>
                  <ul className="space-y-2">
                    {service.features.map((feature, i) => (
                      <li key={i} className="flex items-start text-sm text-gray-700">
                        <svg className="w-4 h-4 mt-1 mr-2 text-accent flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        {feature}
                      </li>
                    ))}
                  </ul>
                  <a 
                    href="/contact" 
                    className="mt-4 inline-block text-accent font-medium hover:text-accent-dark transition-colors flex items-center"
                  >
                    Learn more
                    <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <CallToActionBanner />
    </div>
  );
};

export default ServicesPage;
